import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Users, TrendingUp, AlertTriangle, Target, ThumbsUp,
  Clock, FileText, Loader2, ChevronLeft, ArrowUpRight,
  ArrowDownRight, Phone, Minus, Filter, Calendar
} from 'lucide-react';

function ScoreBadge({ score }) {
  let color = '#ef4444';
  let label = 'Bajo';
  if (score >= 75) { color = '#22c55e'; label = 'Excelente'; }
  else if (score >= 55) { color = '#3b82f6'; label = 'Bueno'; }
  else if (score >= 35) { color = '#f59e0b'; label = 'Regular'; }

  return (
    <div className="flex items-center gap-2">
      <div className="relative w-10 h-10">
        <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" stroke="var(--border-color)" strokeWidth="3" />
          <circle cx="18" cy="18" r="15" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${score * 0.942} 100`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold font-mono" style={{ color }}>
          {Math.round(score)}
        </span>
      </div>
      <span className="text-[10px] font-semibold uppercase" style={{ color }}>{label}</span>
    </div>
  );
}

function MiniChart({ data, dataKey = 'sessions', color = 'var(--accent-primary)', height = 32 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const w = 100;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * w},${height - (v / max) * height}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatCard({ label, value, icon: Icon, color, subtext }) {
  return (
    <div className="rounded-xl border p-4 flex items-start gap-3" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: color + '20' }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold font-mono leading-none" style={{ color: 'var(--text-primary)' }}>{value}</p>
        <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
        {subtext && <p className="text-[10px] mt-0.5 font-medium" style={{ color }}>{subtext}</p>}
      </div>
    </div>
  );
}

function AgentDetailView({ agentKey, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api.get(`/api/analytics/agents/${agentKey}`);
        setDetail(data);
      } catch (err) { toast.error('Error cargando detalle del agente'); onBack(); }
      setLoading(false);
    })();
  }, [agentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>;
  if (!detail) return null;

  const m = detail.metrics;
  const avgMin = Math.floor(m.avg_duration_seconds / 60);
  const avgSec = Math.round(m.avg_duration_seconds % 60);

  return (
    <div data-testid="agent-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium mb-4 transition-colors hover:opacity-80" style={{ color: 'var(--accent-primary)' }} data-testid="back-to-agents">
        <ChevronLeft size={16} /> Volver al ranking
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
            {detail.agent_name?.[0] || 'A'}
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl" style={{ color: 'var(--text-primary)' }}>{detail.agent_name}</h2>
            {detail.user_email && <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{detail.user_email}</p>}
          </div>
        </div>
        <ScoreBadge score={m.score} />
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Sesiones" value={m.total_sessions} icon={FileText} color="var(--accent-primary)" />
        <StatCard label="Duracion media" value={`${avgMin}m ${avgSec}s`} icon={Clock} color="#8b5cf6" />
        <StatCard label="Errores" value={m.error_count} icon={AlertTriangle} color="#ef4444" subtext={m.total_suggestions > 0 ? `${Math.round(m.error_count / m.total_suggestions * 100)}% del total` : ''} />
        <StatCard label="Oportunidades" value={m.opportunity_count} icon={Target} color="#f59e0b" />
        <StatCard label="Palabras totales" value={m.total_words.toLocaleString()} icon={FileText} color="#64748b" />
        <StatCard label="Tasa aceptacion" value={`${m.acceptance_rate}%`} icon={ThumbsUp} color="#22c55e" />
        <StatCard label="Sugerencias totales" value={m.total_suggestions} icon={TrendingUp} color="#3b82f6" />
        <StatCard label="Alta urgencia" value={m.high_urgency_count} icon={AlertTriangle} color="#ef4444" />
      </div>

      {/* Chart */}
      {detail.chart_data && detail.chart_data.length > 1 && (
        <div className="rounded-xl border p-5 mb-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
          <h3 className="text-xs font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Actividad reciente</h3>
          <div className="flex items-end gap-1" style={{ height: '120px' }}>
            {detail.chart_data.map((d, i) => {
              const maxS = Math.max(...detail.chart_data.map(x => x.sessions), 1);
              const hS = (d.sessions / maxS) * 100;
              const hE = detail.chart_data.length > 0 ? (d.errors / Math.max(...detail.chart_data.map(x => x.errors || 0), 1)) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.sessions} sesiones, ${d.errors || 0} errores`}>
                  <div className="w-full flex gap-px items-end" style={{ height: '100px' }}>
                    <div className="flex-1 rounded-t" style={{ height: `${Math.max(hS, 4)}%`, backgroundColor: 'var(--accent-primary)', opacity: 0.8 }} />
                    {d.errors > 0 && <div className="flex-1 rounded-t" style={{ height: `${Math.max(hE, 4)}%`, backgroundColor: '#ef4444', opacity: 0.6 }} />}
                  </div>
                  {i % Math.ceil(detail.chart_data.length / 7) === 0 && (
                    <span className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{d.date.slice(5)}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}><span className="w-2 h-2 rounded" style={{ backgroundColor: 'var(--accent-primary)' }} /> Sesiones</span>
            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}><span className="w-2 h-2 rounded bg-red-500" /> Errores</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Errors */}
        {detail.top_errors && detail.top_errors.length > 0 && (
          <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
            <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Errores frecuentes</h3>
            <div className="space-y-2">
              {detail.top_errors.map((e, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                  <span className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{e.product}</span>
                  <span className="text-xs font-bold font-mono text-red-400 shrink-0 ml-2">{e.count}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Sessions */}
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
          <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Ultimas sesiones</h3>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
            {detail.recent_sessions.map(s => (
              <button key={s.id} onClick={() => navigate(`/sessions/${s.id}`)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors hover:opacity-80"
                style={{ backgroundColor: 'var(--surface-secondary)' }} data-testid={`session-link-${s.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {new Date(s.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                    </span>
                    {s.source === 'twilio_live' && <Phone size={10} className="text-blue-400" />}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {s.status === 'active' ? 'Activa' : 'OK'}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{s.word_count} palabras</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.error_count > 0 && <span className="text-[10px] font-bold text-red-400">{s.error_count} err</span>}
                  <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{s.suggestion_count} sug</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function AgentAnalyticsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/analytics/agents';
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length) url += '?' + params.join('&');
      const data = await api.get(url);
      setAgents(data);
    } catch (err) { toast.error('Error cargando analitica'); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (selectedAgent) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="analytics-page">
        <AgentDetailView agentKey={selectedAgent} onBack={() => setSelectedAgent(null)} />
      </div>
    );
  }

  // Global stats
  const totalSessions = agents.reduce((a, b) => a + b.total_sessions, 0);
  const avgScore = agents.length > 0 ? Math.round(agents.reduce((a, b) => a + b.score, 0) / agents.length) : 0;
  const totalErrors = agents.reduce((a, b) => a + b.error_count, 0);
  const totalOpps = agents.reduce((a, b) => a + b.opportunity_count, 0);

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Analitica de Agentes</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Rendimiento, metricas y ranking de todos los agentes</p>
        </div>
        {/* Date filters */}
        <div className="flex items-center gap-2">
          <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from"
            className="px-2 py-1.5 rounded-lg border text-xs outline-none"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          <Minus size={10} style={{ color: 'var(--text-muted)' }} />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to"
            className="px-2 py-1.5 rounded-lg border text-xs outline-none"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Agentes activos" value={agents.length} icon={Users} color="var(--accent-primary)" />
        <StatCard label="Score medio" value={avgScore} icon={TrendingUp} color={avgScore >= 55 ? '#22c55e' : '#f59e0b'} />
        <StatCard label="Sesiones totales" value={totalSessions} icon={FileText} color="#8b5cf6" />
        <StatCard label="Errores / Oportunidades" value={`${totalErrors} / ${totalOpps}`} icon={Target} color="#3b82f6" />
      </div>

      {/* Agent Ranking */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
          <Users size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>Sin datos de agentes</p>
          <p className="text-sm">Los agentes apareceran aqui cuando creen sesiones de llamada</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
          <table className="w-full" data-testid="agents-table">
            <thead>
              <tr style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>#</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Agente</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Score</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Sesiones</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Errores</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Oportunidades</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Aceptacion</th>
                <th className="text-center px-5 py-3 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>Tendencia</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, idx) => (
                <tr key={agent.agent_key}
                  onClick={() => setSelectedAgent(agent.agent_key)}
                  className="border-t cursor-pointer transition-colors hover:opacity-90"
                  style={{ borderColor: 'var(--border-color)' }}
                  data-testid={`agent-row-${idx}`}>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-bold font-mono w-6 h-6 rounded-full inline-flex items-center justify-center ${
                      idx === 0 ? 'bg-amber-500/20 text-amber-400' : idx === 1 ? 'bg-gray-400/20 text-gray-300' : idx === 2 ? 'bg-orange-500/20 text-orange-400' : ''
                    }`} style={idx > 2 ? { color: 'var(--text-muted)' } : {}}>
                      {idx + 1}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ backgroundColor: 'var(--accent-primary)' }}>
                        {agent.agent_name?.[0] || 'A'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{agent.agent_name}</p>
                        <p className="text-[10px] font-mono truncate" style={{ color: 'var(--text-muted)' }}>
                          {agent.user_email || agent.agent_key}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <ScoreBadge score={agent.score} />
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-sm font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{agent.total_sessions}</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-sm font-mono font-medium ${agent.error_count > 0 ? 'text-red-400' : ''}`} style={agent.error_count === 0 ? { color: 'var(--text-muted)' } : {}}>
                      {agent.error_count}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-sm font-mono font-medium text-amber-400">{agent.opportunity_count}</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-sm font-mono font-medium" style={{ color: agent.acceptance_rate >= 50 ? '#22c55e' : 'var(--text-muted)' }}>
                      {agent.acceptance_rate}%
                    </span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell" style={{ width: '120px' }}>
                    <MiniChart data={agent.trend_data} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
